import { BaseAPI } from "@utils/base-api";
import { HttpException } from "@nestjs/common";

import { EStatusCodes } from "@/constants/state";
import { DiscountApplication } from "@/primary-models";
import { TCreditReason } from "@/constants/discounts-and-credit";

import { TResponse as TRequestResponse } from "../../../api.types";

import { OrderType, SubscriptionType } from "./types/subscriptions";
import { IPossibleDeliveryDates } from "./types/delivery-dates";
import { ExtraProduct } from "./types/products";
import { TLegacyOrderCollection } from "./types/orders";

export class CustomerAPI extends BaseAPI {

  public async addDiscountCode(
    discountCode?: string,
  ): Promise<DiscountApplication> {
    if (discountCode === undefined) {
      throw new HttpException({ message: "No discount code" }, EStatusCodes.NOT_ACCEPTABLE);
    }

    try {
      const response = await this.axios.post(
        `/customer/discount/add?discountCode=${encodeURIComponent(discountCode)}`,

      );
      return response.data;
    } catch {
      throw new HttpException({ message: "Bad request" }, EStatusCodes.BAD_REQUEST);
    }

  }

  /** If a parameter is not set, then it is treated as if all the types apply.
   *  So if 2022-12-06 is blocked for fresh and 2022-12-07 is blocked for addon and
   *  subscriptionType is not set, then neither 2022-12-06 nor 2022-12-07 will be
   *  available in the returned data.
   *  @param orderType
   *  @param numberOfDays
   *  @param subscriptionType
   *  @param addonPlanType
   *  @param bankHoliday if set to true, then only bank holidays will be excluded. Else
   *  non-bank holiday delivery dates that have been disabled will be excluded as well.
   *  Should probably be false for most use cases. */
  public async getPossibleDeliveryDatesType(
    orderType: OrderType,
    numberOfDays?: number,
    subscriptionType?: SubscriptionType,
    addonPlanType?: ExtraProduct,
    bankHoliday?: boolean,
  ): Promise<IPossibleDeliveryDates> {
    const params = new URLSearchParams();
    params.set("orderType", orderType);
    if (numberOfDays) { params.set("numberOfDays", numberOfDays.toString()); }
    if (subscriptionType) { params.set("subscriptionType", subscriptionType); }
    if (addonPlanType) { params.set("addonPlanType", addonPlanType); }
    if (bankHoliday) { params.set("bankHoliday", "true"); }
    const result = await this.axios.get("/getPossibleDeliveryDatesType", { params });
    return result.data;
  }

  public async visitedPage(
    pageUrl: string,
    token: string,
  ): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/visitedPage",
      { pageUrl },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return result.data;
  }

  public async getAllOrders(): Promise<TLegacyOrderCollection> {
    const result = await this.axios.get(
      "/customer/orders/all",
    );
    return result.data;
  }

  public async getLegacyLogs(customerId: string): TRequestResponse<LegacyCustomerEventLog[]> {
    try {
      const response = await this.axios.get(
        `/admin/customer/actions?customerId=${customerId}`,
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  // This endpoint is used to refund a user only for ReCharge charges (not KatKin charges)
  // KatKin charges are refunded using the ChargesAdminAPI.createRefund endpoint
  public async refundUser(
    data: IRefundUserRequest,
  ): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        `/admin/customer/refund?chargeId=${data.rechargeChargeId}&amount=${data.refundAmount}&cancelOrder=${data.cancelOrder}&cxUser=${data.cxUser}`,
        {
          refundReason: data.refundReason,
          ticketUrl: data.ticketUrl,
        },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async resetPassword(email: string): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        "/customer/recover",
        { email },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

}

export interface IRefundUserRequest {
  rechargeChargeId?: number,
  katkinChargeId?: number,
  refundAmount: number,
  cancelOrder: boolean,
  refundReason: TCreditReason,
  ticketUrl: string,
  cxUser: string;
}

export type LegacyCustomerEventLog = {
  id: number;
  customerId: string;
  name: string;
  metadata: string | null | undefined;
  createdAt: string;
};
